/**
 * Lists Service
 *
 * Handles Lists/Segments with advanced query builder functionality.
 * Lists can be dynamic (calculated from query criteria) or static (fixed membership).
 */

import { supabase } from "./supabase";
import { fetchAllRows, pageByNameThenId } from "../utils/fetchAllRows";
import { resolveExportColumns, rowsToCsv } from "../utils/segmentCsv";
import type {
  List,
  CreateListInput,
  UpdateListInput,
  QueryCriteria,
  QueryCondition,
  QueryGroup,
  ListWithContacts,
} from "../types/list.types";
import type { UUID } from "../types/common.types";
import type { Contact } from "../types/contact.types";

// =====================================================
// LIST CRUD OPERATIONS
// =====================================================

/**
 * Get all lists.
 *
 * Returns the cached `contact_count` and `last_calculated` columns directly.
 * Fresh counts come from the explicit refresh action on the Segments page,
 * which calls `refreshListCount`. No hydration fan-out happens on page load
 * regardless of how many segments exist.
 */
export async function getLists(
  organizationId?: UUID,
  options: { includeArchived?: boolean } = {},
): Promise<List[]> {
  // PostgREST caps every response at 1000 rows (max_rows), and PROD has more
  // segments than that. A single select silently dropped every segment past
  // ~#1000 by name, so late names ("Tanner Gun Show ...") never appeared on
  // the Segments page or in any picker built on getLists. Read all pages.
  try {
    return await fetchAllRows<List>((from, to) => {
      let query = supabase.from("lists").select("*");
      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }
      // Archived segments (migration 1470) are hidden from every picker; only
      // the Segments page asks for them (to show and unarchive them).
      if (!options.includeArchived) {
        query = query.is("archived_at", null);
      }
      return pageByNameThenId(query, from, to);
    });
  } catch (error) {
    console.error("Error fetching lists:", error);
    throw error;
  }
}

/**
 * Get a single list by ID
 */
export async function getListById(id: UUID): Promise<List | null> {
  const { data, error } = await supabase
    .from("lists")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching list:", error);
    throw error;
  }

  return data;
}

/**
 * Create a new list
 */
export async function createList(
  input: CreateListInput,
  userId: UUID,
): Promise<List> {
  const { data, error } = await supabase
    .from("lists")
    .insert({
      name: input.name,
      description: input.description,
      organization_id: input.organization_id,
      query_criteria: input.query_criteria,
      is_dynamic: input.is_dynamic,
      created_by: userId,
      updated_by: userId,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating list:", error);
    throw error;
  }

  // INTENTIONAL — initial count is computed via the refresh RPC at
  // creation time so the new segment's card shows a real number rather
  // than "0 / Never calculated". This is the one place outside the
  // explicit Refresh button where a count is auto-computed; it's tied
  // to a user-initiated create action, not a page-load fan-out.
  // Fail-soft: a creation shouldn't fail because the count couldn't be
  // computed (e.g., user lacks role to refresh, or criteria is
  // un-evaluable). The cached `contact_count` defaults to 0 and the
  // user can refresh later.
  if (input.is_dynamic) {
    try {
      await refreshListCount(data.id);
    } catch (err) {
      console.warn(`Initial count refresh failed for list ${data.id}:`, err);
    }
  }

  return data;
}

/**
 * Update an existing list.
 *
 * If query_criteria changed for a dynamic list, null `last_calculated` so
 * the Segments page surfaces the cached count as stale ("Not calculated
 * yet — refresh to update"). The user explicitly clicks Refresh to recompute.
 */
export async function updateList(
  id: UUID,
  updates: UpdateListInput,
  userId: UUID,
): Promise<List> {
  const willStaleCount = Object.prototype.hasOwnProperty.call(
    updates,
    "query_criteria",
  );

  const { data, error } = await supabase
    .from("lists")
    .update({
      ...updates,
      updated_by: userId,
      ...(willStaleCount ? { last_calculated: null } : {}),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating list:", error);
    throw error;
  }

  return data;
}

/**
 * Delete a list
 */
export async function deleteList(id: UUID): Promise<void> {
  const { error } = await supabase.from("lists").delete().eq("id", id);

  if (error) {
    console.error("Error deleting list:", error);
    throw error;
  }
}

/**
 * Get list with contacts
 */
export async function getListWithContacts(
  id: UUID,
): Promise<ListWithContacts | null> {
  const list = await getListById(id);
  if (!list) {
    return null;
  }

  let contacts: Contact[] = [];

  if (list.is_dynamic) {
    // Dynamic list - calculate from query criteria
    contacts = await executeListQuery(
      list.query_criteria,
      list.organization_id,
    );
  } else {
    // Static list - get from list_contacts junction table
    const { data, error } = await supabase
      .from("list_contacts")
      .select(
        `
        contact_id,
        contacts (*)
      `,
      )
      .eq("list_id", id);

    if (error) {
      console.error("Error fetching list contacts:", error);
      throw error;
    }

    // Type the list contact join result
    interface ListContactRow {
      contact_id: UUID;
      contacts: Contact | null;
    }
    const rows = (data || []) as ListContactRow[];
    contacts = rows
      .map((lc) => lc.contacts)
      .filter((c): c is Contact => c !== null);
  }

  return {
    ...list,
    contact_count: contacts.length,
    contacts,
  };
}

/**
 * Recompute and persist a list's contact_count via the server-side
 * `refresh_list_count` RPC. Role-gated to admin/superadmin/manager (the
 * RPC enforces independently). For dynamic lists with criteria the RPC
 * can't safely compile (unsupported field/operator combo), surfaces a
 * `CRITERIA_NOT_EVALUABLE` error to the caller; the cached count is
 * preserved in that case.
 *
 * Returns the updated row so callers can update local state directly
 * without a follow-up fetch.
 */
export interface RefreshListCountResult {
  list_id: UUID;
  is_dynamic: boolean;
  contact_count: number;
  last_calculated: string;
}

export async function refreshListCount(
  listId: UUID,
): Promise<RefreshListCountResult> {
  const { data, error } = await supabase.rpc("refresh_list_count", {
    p_list_id: listId,
  });
  if (error) {
    console.error(`refresh_list_count(${listId}) failed:`, error);
    throw error;
  }
  return data as RefreshListCountResult;
}

// =====================================================
// QUERY EXECUTION ENGINE
// =====================================================

/**
 * Field name mapping from QueryField to actual database column
 * Handles cases where the UI field name differs from the DB column
 */
const FIELD_TO_COLUMN: Record<string, string> = {
  phone: "phone_number",
};

/**
 * Map QueryField to actual database column name
 */
function getColumnName(field: string): string {
  return FIELD_TO_COLUMN[field] || field;
}

/**
 * Escape special characters in values for PostgREST filter strings
 */
function escapeFilterValue(value: string): string {
  // PostgREST uses comma and parentheses as delimiters
  // Escape them by wrapping value in quotes if needed
  if (value.includes(",") || value.includes("(") || value.includes(")")) {
    return `"${value.replace(/"/g, '\\"')}"`;
  }
  return value;
}

/**
 * Array columns in the contacts table — these need special PostgREST
 * operators (cs / not.cs) instead of scalar eq / neq.
 */
const ARRAY_FIELDS = new Set([
  "tags",
  "contact_types",
  "product_tags",
  "product_names",
]);

/**
 * Build a PostgREST filter string for a single condition
 * Returns format like "state.eq.Texas" or "email.ilike.%@gmail.com%"
 */
function buildConditionFilter(condition: QueryCondition): string {
  const column = getColumnName(condition.field);
  const { operator, value } = condition;

  // Array columns need contains (cs) instead of eq/neq
  if (ARRAY_FIELDS.has(column)) {
    switch (operator) {
      case "equals":
        return `${column}.cs.{${escapeFilterValue(String(value))}}`;
      case "not_equals":
        return `${column}.not.cs.{${escapeFilterValue(String(value))}}`;
    }
  }

  switch (operator) {
    case "equals":
      if (typeof value === "boolean") {
        return `${column}.is.${value}`;
      }
      return `${column}.eq.${escapeFilterValue(String(value))}`;

    case "not_equals":
      if (typeof value === "boolean") {
        return `${column}.is.${!value}`;
      }
      return `${column}.neq.${escapeFilterValue(String(value))}`;

    // NOTE: inside a raw PostgREST .or()/.and() filter STRING the wildcard is '*', not '%'. A '%' here is a
    // literal percent, so OR-group string conditions silently matched nobody (and not_contains everybody).
    // The method-form path (applyConditionToQuery, used for AND groups) correctly uses '%' via query.ilike().
    case "contains":
      return `${column}.ilike.*${escapeFilterValue(String(value))}*`;

    case "not_contains":
      return `${column}.not.ilike.*${escapeFilterValue(String(value))}*`;

    case "starts_with":
      return `${column}.ilike.${escapeFilterValue(String(value))}*`;

    case "ends_with":
      return `${column}.ilike.*${escapeFilterValue(String(value))}`;

    case "in":
      if (Array.isArray(value)) {
        const values = value.map((v) => escapeFilterValue(String(v))).join(",");
        return `${column}.in.(${values})`;
      }
      return `${column}.eq.${escapeFilterValue(String(value))}`;

    case "not_in":
      if (Array.isArray(value)) {
        const values = value.map((v) => escapeFilterValue(String(v))).join(",");
        return `${column}.not.in.(${values})`;
      }
      return `${column}.neq.${escapeFilterValue(String(value))}`;

    case "greater_than":
      return `${column}.gt.${escapeFilterValue(String(value))}`;

    case "less_than":
      return `${column}.lt.${escapeFilterValue(String(value))}`;

    case "greater_than_or_equal":
      return `${column}.gte.${escapeFilterValue(String(value))}`;

    case "less_than_or_equal":
      return `${column}.lte.${escapeFilterValue(String(value))}`;

    case "is_null":
      return `${column}.is.null`;

    case "is_not_null":
      return `${column}.not.is.null`;

    case "between":
      if (Array.isArray(value) && value.length === 2) {
        const [min, max] = value;
        return `and(${column}.gte.${escapeFilterValue(String(min))},${column}.lte.${escapeFilterValue(String(max))})`;
      }
      throw new Error(`Invalid 'between' value: expected [min, max] array`);

    default:
      throw new Error(`Unsupported operator: ${operator}`);
  }
}

/**
 * Check if item is a QueryCondition (has 'field' property) vs QueryGroup (has 'operator')
 */
function isQueryCondition(
  item: QueryCondition | QueryGroup,
): item is QueryCondition {
  return "field" in item;
}

/**
 * Build a PostgREST filter string for a group (AND/OR)
 * Recursively handles nested groups
 */
function buildGroupFilter(group: QueryGroup): string {
  if (!group.conditions || group.conditions.length === 0) {
    return "";
  }

  const filterParts: string[] = [];

  for (const item of group.conditions) {
    if (isQueryCondition(item)) {
      filterParts.push(buildConditionFilter(item));
    } else {
      // Nested group - recursively build
      const nestedFilter = buildGroupFilter(item);
      if (nestedFilter) {
        filterParts.push(nestedFilter);
      }
    }
  }

  if (filterParts.length === 0) {
    return "";
  }

  if (filterParts.length === 1) {
    return filterParts[0];
  }

  // Join with AND or OR
  if (group.operator === "AND") {
    return `and(${filterParts.join(",")})`;
  } else {
    return `or(${filterParts.join(",")})`;
  }
}

/**
 * Apply a single condition to a Supabase query
 * Returns a new query with the condition applied
 */
function applyConditionToQuery(
  query: ReturnType<typeof supabase.from>,
  condition: QueryCondition,
): ReturnType<typeof supabase.from> {
  const column = getColumnName(condition.field);
  const { operator, value } = condition;

  switch (operator) {
    case "equals":
      if (ARRAY_FIELDS.has(column)) {
        return query.contains(column, [String(value)]);
      }
      if (typeof value === "boolean") {
        return query.is(column, value);
      }
      return query.eq(column, value);

    case "not_equals":
      if (ARRAY_FIELDS.has(column)) {
        return query.not(column, "cs", `{${String(value)}}`);
      }
      if (typeof value === "boolean") {
        return query.is(column, !value);
      }
      return query.neq(column, value);

    case "contains":
      return query.ilike(column, `%${value}%`);

    case "not_contains":
      return query.not(column, "ilike", `%${value}%`);

    case "starts_with":
      return query.ilike(column, `${value}%`);

    case "ends_with":
      return query.ilike(column, `%${value}`);

    case "in":
      if (Array.isArray(value)) {
        return query.in(column, value);
      }
      return query.eq(column, value);

    case "not_in":
      if (Array.isArray(value)) {
        return query.not(column, "in", `(${value.join(",")})`);
      }
      return query.neq(column, value);

    case "greater_than":
      return query.gt(column, value);

    case "less_than":
      return query.lt(column, value);

    case "greater_than_or_equal":
      return query.gte(column, value);

    case "less_than_or_equal":
      return query.lte(column, value);

    case "is_null":
      return query.is(column, null);

    case "is_not_null":
      return query.not(column, "is", null);

    case "between":
      if (Array.isArray(value) && value.length === 2) {
        const [min, max] = value;
        return query.gte(column, min).lte(column, max);
      }
      return query;

    case "within_radius_of_zip":
    case "within_radius_of_org":
    case "within_radius_of_location":
    case "within_radius_of_event":
      // Audit HIGH remediation: proximity/radius predicates are compiled server-side (ST_DWithin via
      // build_strict_segment_sql) and CANNOT be replicated as PostgREST client filters. Silently
      // ignoring them (the default branch) would OVER-export a proximity segment vs its count/send.
      // Fail CLOSED. Counts, refresh, dynamic membership and campaign targeting use the strict compiler
      // and remain accurate; client-side CSV export of a proximity segment is not supported yet.
      throw new Error(
        "This segment uses proximity/radius criteria that are evaluated server-side, so it can't be " +
          "exported to CSV via this path yet. Its contact count and campaign targeting are accurate.",
      );

    default:
      console.warn(`Unsupported operator: ${operator}`);
      return query;
  }
}

/**
 * Apply a group of conditions to a query
 * For AND groups: chains conditions directly
 * For OR groups: uses .or() with PostgREST filter string
 */
function applyGroupToQuery(
  query: ReturnType<typeof supabase.from>,
  group: QueryGroup,
): ReturnType<typeof supabase.from> {
  if (!group.conditions || group.conditions.length === 0) {
    return query;
  }

  if (group.operator === "AND") {
    // AND group: chain conditions directly
    let result = query;
    for (const item of group.conditions) {
      if (isQueryCondition(item)) {
        result = applyConditionToQuery(result, item);
      } else {
        // Nested group
        if (item.operator === "OR") {
          // Nested OR needs to use .or() with filter string
          const filterString = buildGroupFilter(item);
          if (filterString) {
            result = result.or(filterString);
          }
        } else {
          // Nested AND can be applied sequentially
          result = applyGroupToQuery(result, item);
        }
      }
    }
    return result;
  } else {
    // OR group: build filter string and use .or()
    const filterString = buildGroupFilter(group);
    if (filterString) {
      return query.or(filterString);
    }
    return query;
  }
}

/**
 * Behavioral fields that query contact_events instead of contact columns
 */
const BEHAVIORAL_FIELDS = ["event_performed", "event_not_performed"];

/**
 * Extract behavioral conditions from a QueryGroup (recursively).
 * Returns the behavioral conditions and a cleaned group with them removed.
 */
function extractBehavioralConditions(group: QueryGroup): {
  behavioral: { field: string; operator: string; value: any }[];
  cleaned: QueryGroup;
} {
  const behavioral: { field: string; operator: string; value: any }[] = [];
  const cleanedConditions: (QueryCondition | QueryGroup)[] = [];

  for (const item of group.conditions) {
    if (isQueryCondition(item) && BEHAVIORAL_FIELDS.includes(item.field)) {
      behavioral.push({
        field: item.field,
        operator: item.operator,
        value: item.value,
      });
    } else if (!isQueryCondition(item)) {
      // Recursively extract from nested groups
      const nested = extractBehavioralConditions(item);
      behavioral.push(...nested.behavioral);
      if (nested.cleaned.conditions.length > 0) {
        cleanedConditions.push(nested.cleaned);
      }
    } else {
      cleanedConditions.push(item);
    }
  }

  return {
    behavioral,
    cleaned: { ...group, conditions: cleanedConditions },
  };
}

/**
 * Resolve behavioral conditions to a set of contact IDs
 * by querying the contact_events table
 */
async function resolveBehavioralContactIds(
  conditions: { field: string; operator: string; value: any }[],
): Promise<{ includeIds: UUID[] | null; excludeIds: UUID[] | null }> {
  let includeIds: Set<UUID> | null = null;
  let excludeIds: Set<UUID> | null = null;

  for (const cond of conditions) {
    const eventType = Array.isArray(cond.value)
      ? cond.value[0]
      : (cond.value as string);

    if (!eventType) {
      continue;
    }

    let eventQuery = supabase
      .from("contact_events")
      .select("contact_id")
      .eq("event_type", eventType);

    // Apply within_days filter
    if (cond.operator === "within_days") {
      const days = Array.isArray(cond.value) ? parseInt(cond.value[1], 10) : 30;
      if (!isNaN(days) && days > 0) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        eventQuery = eventQuery.gte("occurred_at", cutoff.toISOString());
      }
    }

    const { data: events, error } = await eventQuery;
    if (error) {
      console.error("Error querying contact_events:", error);
      continue;
    }

    const matchedIds = new Set(
      (events || []).map((e: { contact_id: UUID }) => e.contact_id),
    );

    if (cond.field === "event_performed") {
      // Intersection: contacts must have done this event
      if (includeIds === null) {
        includeIds = matchedIds;
      } else {
        // Intersect with previous include sets
        includeIds = new Set(
          [...includeIds].filter((id) => matchedIds.has(id)),
        );
      }
    } else {
      // event_not_performed: exclude contacts who have done this event
      if (excludeIds === null) {
        excludeIds = matchedIds;
      } else {
        // Union: exclude anyone who has done any of the "not performed" events
        for (const id of matchedIds) {
          excludeIds.add(id);
        }
      }
    }
  }

  return {
    includeIds: includeIds ? Array.from(includeIds) : null,
    excludeIds: excludeIds ? Array.from(excludeIds) : null,
  };
}

/**
 * Execute query criteria to get matching contacts
 * Builds a complete Supabase filter from the QueryCriteria
 * Supports behavioral conditions via contact_events subqueries
 */
export async function executeListQuery(
  criteria: QueryCriteria,
  organizationId?: UUID,
): Promise<Contact[]> {
  // Extract behavioral conditions for separate processing
  const { behavioral, cleaned } = extractBehavioralConditions(criteria);

  // Resolve behavioral conditions to contact ID sets
  let behavioralInclude: UUID[] | null = null;
  let behavioralExclude: UUID[] | null = null;

  if (behavioral.length > 0) {
    const resolved = await resolveBehavioralContactIds(behavioral);
    behavioralInclude = resolved.includeIds;
    behavioralExclude = resolved.excludeIds;

    // If include set is empty, no contacts match
    if (behavioralInclude !== null && behavioralInclude.length === 0) {
      return [];
    }
  }

  let query = supabase.from("contacts").select("*");

  // Always filter by organization if provided
  if (organizationId) {
    query = query.eq("organization_id", organizationId);
  }

  // Apply behavioral include/exclude filters
  if (behavioralInclude !== null) {
    query = query.in("id", behavioralInclude);
  }
  if (behavioralExclude !== null && behavioralExclude.length > 0) {
    query = query.not("id", "in", `(${behavioralExclude.join(",")})`);
  }

  // Build and apply the filter from remaining query criteria
  if (cleaned.conditions && cleaned.conditions.length > 0) {
    query = applyGroupToQuery(query, cleaned);
  }

  // Exclude archived contacts by default
  query = query.eq("is_archived", false);

  const { data, error } = await query;

  if (error) {
    console.error("Error executing list query:", error);
    console.error("Filter attempted:", criteria);
    throw error;
  }

  return data || [];
}

/**
 * Stream a list's contacts to a CSV blob suitable for browser download.
 * Pages through the data 1000 rows at a time so we stay within PostgREST's
 * default range limit and don't materialise everything in one shot. Caller
 * is responsible for triggering the download.
 *
 * Hard cap: 250_000 rows. Anything larger should go through a server-side
 * streaming endpoint (Phase 2 of segments-CSV work). The cap is here so a
 * mistakenly-clicked download doesn't hang the browser on a million-row
 * segment.
 */
const CSV_PAGE_SIZE = 1000;
const CSV_MAX_ROWS = 250_000;

/**
 * Carries the server correlation ID so the operator can quote it to support instead of being told
 * to open the browser console. The `message` is only ever the server's own opaque text or a
 * status-derived string — never raw SQL/PostgREST detail.
 */
export class SegmentCsvExportError extends Error {
  readonly correlationId?: string;
  readonly status?: number;
  constructor(
    message: string,
    opts: { correlationId?: string; status?: number } = {},
  ) {
    super(message);
    this.name = "SegmentCsvExportError";
    this.correlationId = opts.correlationId;
    this.status = opts.status;
  }
}

async function downloadDynamicSegmentCsv(
  list: List,
  columns: ReturnType<typeof resolveExportColumns>,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ rowCount: number; truncated: boolean; filename: string }> {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (sessionError || !accessToken) {
    throw sessionError ?? new Error("An authenticated session is required");
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const response = await fetch(
    `${supabaseUrl}/functions/v1/export-segment-csv`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        "Content-Type": "application/json",
        "x-application-name": "uslawshield-crm",
      },
      body: JSON.stringify({
        list_id: list.id,
        fields: columns.map((column) => column.key),
      }),
    },
  );

  if (!response.ok) {
    let message = `CSV export failed (${response.status})`;
    let correlationId: string | undefined;
    try {
      const body = (await response.json()) as {
        error?: string;
        correlationId?: string;
      };
      if (body.error) {
        message = body.error;
      }
      if (typeof body.correlationId === "string") {
        correlationId = body.correlationId;
      }
    } catch {
      // Non-JSON gateway/timeout response: keep the status-based message and no correlation id.
    }
    throw new SegmentCsvExportError(message, {
      correlationId,
      status: response.status,
    });
  }

  const csv = await response.text();
  const parsedRowCount = Number(response.headers.get("X-Row-Count"));
  const rowCount = Number.isFinite(parsedRowCount) ? parsedRowCount : 0;
  const truncated = response.headers.get("X-Truncated") === "true";
  const filename =
    response.headers.get("X-Filename") ||
    `${list.name.replace(/[^a-z0-9_-]+/gi, "_")}-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  onProgress?.(rowCount, rowCount);
  return { rowCount, truncated, filename };
}

export async function downloadSegmentCsv(
  listId: UUID,
  options: {
    onProgress?: (loaded: number, total: number) => void;
    fields?: string[];
  } = {},
): Promise<{ rowCount: number; truncated: boolean; filename: string }> {
  const list = await getListById(listId);
  if (!list) {
    throw new Error("Segment not found");
  }

  // Resolve export columns from the whitelist (order = user selection; fallback = default set).
  const columns = resolveExportColumns(options.fields);

  // EVERY dynamic segment exports through the strict server-side compiler.
  //
  // This used to be gated on requiresServerSegmentCsv(), which only diverted segments containing a
  // proximity operator; everything else was re-implemented in the browser by translating each
  // criterion into a physical `contacts` column. Compiler-backed VIRTUAL fields (email_quality,
  // opened_email_30d, and every engagement/Zuora-backed predicate) are not columns on `contacts`,
  // so those exports failed outright — or, worse, silently produced different membership from the
  // preview and the campaign send.
  //
  // The fix is structural rather than another allow-list: there is now exactly one implementation
  // of dynamic membership (build_strict_segment_sql via stream_dynamic_segment_page), so a new
  // criterion type cannot diverge here again. Static lists keep the paged browser export below,
  // where membership is an explicit list_contacts table and no compiler is involved.
  if (list.is_dynamic) {
    return downloadDynamicSegmentCsv(list, columns, options.onProgress);
  }

  const { count, error: countError } = await supabase
    .from("list_contacts")
    .select("contact_id", { count: "exact", head: true })
    .eq("list_id", listId);
  if (countError) {
    throw countError;
  }
  const total = count ?? 0;

  const targetRows = Math.min(total, CSV_MAX_ROWS);
  const truncated = total > CSV_MAX_ROWS;

  const allContacts: Contact[] = [];
  let offset = 0;
  while (offset < targetRows) {
    const pageEnd = Math.min(offset + CSV_PAGE_SIZE, targetRows) - 1;
    const { data, error } = await supabase
      .from("list_contacts")
      .select("contact_id, contacts (*)")
      .eq("list_id", listId)
      .order("contact_id", { ascending: true })
      .range(offset, pageEnd);
    if (error) {
      throw new Error(`CSV page failed at offset ${offset}: ${error.message}`);
    }
    const pageContacts: Contact[] = (data || [])
      .map((r: { contact_id: UUID; contacts: Contact | null }) => r.contacts)
      .filter((c: Contact | null): c is Contact => c !== null);

    if (pageContacts.length === 0) {
      break;
    }
    allContacts.push(...pageContacts);
    offset += pageContacts.length;
    options.onProgress?.(allContacts.length, targetRows);
    // Pages smaller than expected → no more rows
    if (pageContacts.length < CSV_PAGE_SIZE) {
      break;
    }
  }

  const csv = rowsToCsv(
    allContacts as unknown as Array<Record<string, unknown>>,
    columns,
  );
  const filename = `${list.name.replace(/[^a-z0-9_-]+/gi, "_")}-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;

  // Trigger a browser download.
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rowCount: allContacts.length, truncated, filename };
}

/**
 * Preview list query results (count only, more efficient)
 */
export async function previewListQueryCount(
  criteria: QueryCriteria,
  organizationId?: UUID,
): Promise<number> {
  // Extract behavioral conditions
  const { behavioral, cleaned } = extractBehavioralConditions(criteria);

  let behavioralInclude: UUID[] | null = null;
  let behavioralExclude: UUID[] | null = null;

  if (behavioral.length > 0) {
    const resolved = await resolveBehavioralContactIds(behavioral);
    behavioralInclude = resolved.includeIds;
    behavioralExclude = resolved.excludeIds;

    if (behavioralInclude !== null && behavioralInclude.length === 0) {
      return 0;
    }
  }

  let query = supabase
    .from("contacts")
    .select("id", { count: "exact", head: true });

  if (organizationId) {
    query = query.eq("organization_id", organizationId);
  }

  if (behavioralInclude !== null) {
    query = query.in("id", behavioralInclude);
  }
  if (behavioralExclude !== null && behavioralExclude.length > 0) {
    query = query.not("id", "in", `(${behavioralExclude.join(",")})`);
  }

  if (cleaned.conditions && cleaned.conditions.length > 0) {
    query = applyGroupToQuery(query, cleaned);
  }

  query = query.eq("is_archived", false);

  const { count, error } = await query;

  if (error) {
    console.error("Error previewing list query:", error);
    throw error;
  }

  return count || 0;
}

// =====================================================
// STATIC LIST CONTACT MANAGEMENT
// =====================================================

/**
 * Add contacts to a static list
 */
export async function addContactsToList(
  listId: UUID,
  contactIds: UUID[],
  userId?: UUID,
): Promise<void> {
  if (contactIds.length === 0) {
    return;
  }

  // Build insert records
  const records = contactIds.map((contactId) => ({
    list_id: listId,
    contact_id: contactId,
    added_by: userId,
  }));

  const { error } = await supabase
    .from("list_contacts")
    .upsert(records, { onConflict: "list_id,contact_id" });

  if (error) {
    console.error("Error adding contacts to list:", error);
    throw error;
  }

  // Update the list's contact count
  await updateStaticListCount(listId);
}

/**
 * Remove contacts from a static list
 */
export async function removeContactsFromList(
  listId: UUID,
  contactIds: UUID[],
): Promise<void> {
  if (contactIds.length === 0) {
    return;
  }

  const { error } = await supabase
    .from("list_contacts")
    .delete()
    .eq("list_id", listId)
    .in("contact_id", contactIds);

  if (error) {
    console.error("Error removing contacts from list:", error);
    throw error;
  }

  // Update the list's contact count
  await updateStaticListCount(listId);
}

/**
 * Get all lists that a contact belongs to (static + dynamic).
 *
 * Single round-trip via `get_contact_segment_memberships` RPC: the server
 * does the static `list_contacts` join + dynamic criteria evaluation in
 * one transaction, applying list.organization_id scoping to dynamic
 * eval. Replaces the prior frontend per-list fan-out which N+1'd one
 * full-contacts SELECT per dynamic list on every contact-detail load.
 *
 * Dynamic lists with un-evaluable criteria (unsupported field/operator
 * combos) are silently excluded from the result — prod has 0 such lists
 * today, and the frontend segment builder steers users away from
 * unsupported combos via the validator's amber warning.
 */
export async function getContactLists(contactId: UUID): Promise<List[]> {
  const { data, error } = await supabase.rpc(
    "get_contact_segment_memberships",
    { p_contact_id: contactId },
  );

  if (error) {
    console.error("Error fetching contact lists:", error);
    throw error;
  }

  return (data ?? []) as List[];
}

/**
 * Get all static lists (for adding contacts to)
 */
export async function getStaticLists(): Promise<List[]> {
  // Paged: static lists alone are near the 1000-row PostgREST cap on PROD.
  try {
    return await fetchAllRows<List>((from, to) =>
      pageByNameThenId(
        supabase
          .from("lists")
          .select("*")
          .eq("is_dynamic", false)
          .is("archived_at", null),
        from,
        to,
      ),
    );
  } catch (error) {
    console.error("Error fetching static lists:", error);
    throw error;
  }
}

/**
 * Update contact count for a static list
 */
async function updateStaticListCount(listId: UUID): Promise<void> {
  const { count, error } = await supabase
    .from("list_contacts")
    .select("id", { count: "exact", head: true })
    .eq("list_id", listId);

  if (error) {
    console.error("Error counting list contacts:", error);
    return;
  }

  await supabase
    .from("lists")
    .update({
      contact_count: count || 0,
      last_calculated: new Date().toISOString(),
    })
    .eq("id", listId);
}

/**
 * Recent additions to a static list — most recent first, default limit 5.
 *
 * Operators use this to confirm test accounts landed in the list and to
 * spot-check inbound form/API traffic ("did anyone get added today?").
 * Dynamic lists have no addition timestamp — caller must guard on
 * `is_dynamic === false` before calling.
 */
export interface StaticListRecentMember {
  contact_id: UUID;
  added_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export async function getRecentStaticListMembers(
  listId: UUID,
  limit = 5,
): Promise<StaticListRecentMember[]> {
  const { data, error } = await supabase
    .from("list_contacts")
    .select(
      `
      contact_id,
      added_at,
      contacts (
        first_name,
        last_name,
        email
      )
    `,
    )
    .eq("list_id", listId)
    .order("added_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching recent static-list members:", error);
    throw error;
  }

  interface Row {
    contact_id: UUID;
    added_at: string;
    contacts: {
      first_name: string | null;
      last_name: string | null;
      email: string | null;
    } | null;
  }
  const rows = (data || []) as Row[];
  return rows.map((r) => ({
    contact_id: r.contact_id,
    added_at: r.added_at,
    first_name: r.contacts?.first_name ?? null,
    last_name: r.contacts?.last_name ?? null,
    email: r.contacts?.email ?? null,
  }));
}

/**
 * Check if a contact is in a specific list
 */
export async function isContactInList(
  listId: UUID,
  contactId: UUID,
): Promise<boolean> {
  const { data, error } = await supabase
    .from("list_contacts")
    .select("id")
    .eq("list_id", listId)
    .eq("contact_id", contactId)
    .maybeSingle();

  if (error) {
    console.error("Error checking list membership:", error);
    return false;
  }

  return data !== null;
}

// =====================================================
// TRIGGER CONFIGURATION (Phase 3)
// =====================================================

/**
 * Get lists with active triggers
 */
export async function getListsWithTriggers(): Promise<List[]> {
  try {
    return await fetchAllRows<List>((from, to) =>
      pageByNameThenId(
        supabase.from("lists").select("*").neq("trigger_type", "NONE"),
        from,
        to,
      ),
    );
  } catch (error) {
    console.error("Error fetching lists with triggers:", error);
    throw error;
  }
}

/**
 * Get lists with trigger info (includes linked offer details)
 */
export async function getListsWithTriggerInfo(): Promise<
  import("../types/list.types").ListWithTriggerInfo[]
> {
  try {
    return await fetchAllRows<
      import("../types/list.types").ListWithTriggerInfo
    >((from, to) =>
      pageByNameThenId(
        supabase.from("v_lists_with_triggers").select("*"),
        from,
        to,
      ),
    );
  } catch (error) {
    console.error("Error fetching lists with trigger info:", error);
    throw error;
  }
}

/**
 * Update list trigger configuration
 */
export async function updateListTrigger(
  listId: UUID,
  triggerConfig: {
    trigger_type?: import("../types/list.types").ListTriggerType;
    trigger_config?: import("../types/list.types").TriggerConfig;
    auto_generate_vouchers?: boolean;
    linked_offer_id?: UUID | null;
    evaluation_interval_minutes?: number;
    is_active?: boolean;
  },
  userId: UUID,
): Promise<List> {
  const { data, error } = await supabase
    .from("lists")
    .update({
      ...triggerConfig,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", listId)
    .select()
    .single();

  if (error) {
    console.error("Error updating list trigger:", error);
    throw error;
  }

  return data;
}

/**
 * Get voucher issuances for a list
 */
export async function getListVoucherIssuances(
  listId: UUID,
): Promise<import("../types/list.types").ListVoucherIssuance[]> {
  const { data, error } = await supabase
    .from("list_voucher_issuances")
    .select("*")
    .eq("list_id", listId)
    .order("issued_at", { ascending: false });

  if (error) {
    console.error("Error fetching list voucher issuances:", error);
    throw error;
  }

  return data || [];
}

/**
 * Check if a voucher has been issued for a contact in a list
 */
export async function hasVoucherBeenIssued(
  listId: UUID,
  contactId: UUID,
  offerId: UUID,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("has_voucher_been_issued", {
    p_list_id: listId,
    p_contact_id: contactId,
    p_offer_id: offerId,
  });

  if (error) {
    console.error("Error checking voucher issuance:", error);
    return false;
  }

  return data === true;
}

/**
 * Manually trigger evaluation for a list
 */
export async function manuallyEvaluateList(listId: UUID): Promise<void> {
  // Update next_evaluation_at to now to trigger immediate evaluation
  const { error } = await supabase
    .from("lists")
    .update({
      next_evaluation_at: new Date().toISOString(),
    })
    .eq("id", listId);

  if (error) {
    console.error("Error triggering list evaluation:", error);
    throw error;
  }
}

// =====================================================
// COMBINED STATIC LISTS (A1 multi-segment combine)
// =====================================================

export interface CombinedAudienceCount {
  total_before_dedup: number;
  deduped_contacts: number;
  excluded_count: number;
}

/**
 * Best-effort preview of the combined (union of includes, minus union of excludes, deduped)
 * audience size for a set of segments — WITHOUT materializing anything. Backed by the
 * `count_combined_segment_audience` SECURITY DEFINER RPC (same build_strict_segment_sql compiler
 * as live send, so no predicate fork). Debounce calls at the UI layer; this can be heavy for
 * large dynamic sources.
 */
export async function countCombinedSegmentAudience(
  includeIds: UUID[],
  excludeIds: UUID[] = [],
): Promise<CombinedAudienceCount> {
  const { data, error } = await supabase.rpc(
    "count_combined_segment_audience",
    {
      p_include: includeIds,
      p_exclude: excludeIds,
    },
  );
  if (error) {
    console.error("Error counting combined audience:", error);
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return {
    total_before_dedup: Number(row?.total_before_dedup ?? 0),
    deduped_contacts: Number(row?.deduped_contacts ?? 0),
    excluded_count: Number(row?.excluded_count ?? 0),
  };
}

/**
 * Create a combined (snapshot) static list from a set of include/exclude segments. The list row is
 * created immediately with build_status='building'; the actual union/difference membership is
 * materialized asynchronously by the process-combined-list-builds drainer (cron). Returns the new
 * list id — poll `getListById(id).build_status` until 'ready' before attaching it to a flight (the
 * DB trigger rejects attaching a non-ready combined list to a scheduling/sending flight).
 */
export async function createCombinedList(
  includeIds: UUID[],
  excludeIds: UUID[],
  name: string,
): Promise<UUID> {
  const { data, error } = await supabase.rpc("create_combined_list", {
    p_include: includeIds,
    p_exclude: excludeIds,
    p_name: name,
  });
  if (error) {
    console.error("Error creating combined list:", error);
    throw error;
  }
  // RPC returns the new list uuid (scalar or single-row).
  const id = Array.isArray(data) ? data[0] : data;
  return (
    typeof id === "object" && id !== null ? (id as { id?: UUID }).id : id
  ) as UUID;
}

/**
 * Poll a list's build_status until it leaves 'building' (→ 'ready' or 'failed'), or the timeout
 * elapses. Resolves with the final List. Used by CombineSegmentsModal after createCombinedList so
 * the flight only ever attaches a fully-materialized list.
 */
export async function waitForListReady(
  listId: UUID,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<List> {
  const intervalMs = opts.intervalMs ?? 3000;
  const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const list = await getListById(listId);
    if (!list) {
      throw new Error("Combined list disappeared while building");
    }
    if (list.build_status !== "building") {
      return list;
    }
    if (Date.now() - start > timeoutMs) {
      throw new Error("Combined list is still building — check back shortly");
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/**
 * Unarchive a segment (migration 1470). A segment whose members were not
 * offloaded yet comes back at once ("live"); an offloaded one is restored from
 * the archive by the database ("restoring") and reappears in the pickers when
 * that finishes.
 */
export async function unarchiveSegment(
  listId: UUID,
): Promise<{ list_id: UUID; state: "live" | "restoring"; note?: string }> {
  const { data, error } = await supabase.rpc("unarchive_segment", {
    p_list_id: listId,
  });
  if (error) {
    throw error;
  }
  return data as { list_id: UUID; state: "live" | "restoring"; note?: string };
}
